import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ContainerWrapper,
  Info,
  ImageWrapper,
  SigninButton,
  BackgroundWrapper,
  SwiperBtn,
  SwiperCard,
  SwiperPaginationWrapper,
} from './style';
import useCurrentLang from '@/hooks/useCurrentLang';
import useResize from '@/hooks/useResize';
import useGetFeatures from '@/hooks/useGetFeatures';
import { getPosts, getBlogSummary } from '@/api/frontData.api';
import PATH from '@/router/path';
import { sify } from 'chinese-conv/dist';
// import Swiper core and required modules
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { H2 } from '@/components/UI/Typography/Heading';
import { Box } from '@/layout';

const Banner = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const { isTablet, isMobile } = useResize();
  const [announcements, setAnnouncements] = useState<BannerPost[]>([]);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const features = useGetFeatures();

  useEffect(() => {
    let active = true;
    // 三種內容互不相依，Blog 不等待 Feature 或 Post 才開始請求與顯示。
    // 回應已包含所有語系，切換語言只需重新呈現文字。
    void getBlogSummary().then(({ data }) => { if (active) setBlogs(data); })
      .catch(() => console.warn('Unable to load homepage blogs.'));
    void getPosts().then(({ data }) => { if (active) setAnnouncements(data); })
      .catch(() => console.warn('Unable to load homepage posts.'));
    return () => { active = false; };
  }, []);

  const posts = useMemo<(BannerFeature | BannerPost | Blog)[]>(() => [
    ...announcements,
    ...features.filter(feature => feature.show === 1).map(feature => ({ ...feature, type: 'feature' as const })),
    ...blogs.filter(blog => blog.show_in_index === 1).map(blog => ({ ...blog, type: 'blog' as const })),
  ].sort((a, b) => a.weight === b.weight
    ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    : b.weight - a.weight), [announcements, features, blogs]);

  return (
    <BackgroundWrapper isTablet={isTablet} isMobile={isMobile}>
      <ContainerWrapper isTablet={isTablet} isMobile={isMobile}>
        <Info lang={lang} isTablet={isTablet} isMobile={isMobile}>
          <div>{t('pages.index.Banner.title')}</div>
          <div>{t('pages.index.Banner.description')}</div>
          <div>
            <Link to="/auth/signin">{t('buttons.signIn')}</Link>
            <Link to="/auth/signup">
              <SigninButton>{t('buttons.register')}</SigninButton>
            </Link>
          </div>
        </Info>
        <SwiperPaginationWrapper isTablet={isTablet} isMobile={isMobile}>
          <div className="swiper-pagination" style={{ position: 'unset' }}></div>
        </SwiperPaginationWrapper>
        <ImageWrapper isTablet={isTablet} isMobile={isMobile}>
          <SwiperBtn className="prevBtn" isTablet={isTablet} isMobile={isMobile}>
            <img src="/images/index/arrow_back.svg" alt="" />
          </SwiperBtn>
          <div className="swiper-container">
            {
              posts.length > 0 && (<Swiper
                centeredSlides={true}
                grabCursor={true}
                modules={[Navigation, Pagination]}
                spaceBetween={0}
                slidesPerView={1}
                navigation={{
                  prevEl: '.prevBtn',
                  nextEl: '.nextBtn',
                }}
                pagination={{
                  el: '.swiper-pagination',
                  type: 'bullets',
                  clickable: true,
                  bulletActiveClass: 'swiper-pagination-bullet-active',
                }}
                onSwiper={(swiper) => { }}
                onSlideChange={() => { }}
                loop={true}
              >
                {posts.map((post, index) => (
                  <SwiperSlide key={index}>
                    <SwiperCard isTablet={isTablet} isMobile={isMobile}>
                      <div className="pic">
                        <img src={post.img_url} alt="" loading={index === 0 ? 'eager' : 'lazy'} decoding="async" />
                      </div>
                      <div className="content">
                        <H2 style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{lang === 'en' ? post.title_en : lang === 'tc' ? post.title : sify(post.title)}</H2>
                        <p>
                          {lang === 'en'
                            ? post.description_en
                            : lang === 'tc'
                              ? post.description
                              : sify(post.description)}
                        </p>
                        {post.type && post.cus_title ? (
                          <Link to={post.type === 'blog' ? PATH.blog.detail.replace(':lang', lang).replace(':cus_title', post.cus_title) : PATH.features.detail.replace(':lang', lang).replace(':id', post.cus_title)}>
                            {t('buttons.howtoUse')}
                          </Link>
                        ) : (
                          <Box paddingTop={'23px'} />
                        )}
                      </div>
                    </SwiperCard>
                  </SwiperSlide>
                ))}
              </Swiper>
              )
            }
          </div>
          <SwiperBtn className="nextBtn" isTablet={isTablet} isMobile={isMobile}>
            <img src="/images/index/arrow_forward.svg" alt="" />
          </SwiperBtn>
        </ImageWrapper>
      </ContainerWrapper>
    </BackgroundWrapper>
  );
};

export default Banner;
